import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineSubtitles,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  GENDER,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const getAge = (birthdate) => {
  if (!birthdate) return 0;
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const Children = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.INFO}
              />
            </Col>
            {get(initialValues, "children", []).map((child, index) => {
              const age = getAge(child.identity?.user?.birthDate);
              const isMajor = age > 18;

              const lastnamePath = `children.${index}.identity.user.lastname`;
              const lastnameField = bindField(lastnamePath, {
                label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
                required: isFieldRequired(lastnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const firstnamePath = `children.${index}.identity.user.firstname`;
              const firstnameField = bindField(firstnamePath, {
                label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
                required: isFieldRequired(firstnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const genderPath = `children.${index}.identity.user.gender`;
              const genderField = bindField(genderPath, {
                label: SUBSCRIPTION.FORM.CHILD_GENDER.LABEL,
                required: isFieldRequired(genderPath),
                onBlur: () => setPercent(initialValues),
              });

              const socialSecurityNumberPath = `children.${index}.identity.socialSecurityNumber`;
              const socialSecurityNumberField = bindField(
                socialSecurityNumberPath,
                {
                  label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
                  required: isFieldRequired(socialSecurityNumberPath),
                  onBlur: () => setPercent(initialValues),
                },
              );

              return (
                <Col lg={24} xs={24} key={`child-form-${index}`}>
                  <Form
                    type="highlight"
                    title={`${SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.CHILD.TITLE} ${index + 1}`}
                  >
                    <Row gutter={[16, 16]}>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={lastnameField.path}
                          label={lastnameField.label}
                          required={lastnameField.required}
                          disabled={lastnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={lastnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER
                              }
                              type="custom"
                              normalizeText={true}
                              defaultValue={lastnameField.defaultValue}
                              {...field}
                              onChange={lastnameField.onChange}
                              onBlur={lastnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={firstnameField.path}
                          label={firstnameField.label}
                          required={firstnameField.required}
                          disabled={firstnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              placeholder={
                                SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER
                              }
                              label={firstnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              type="custom"
                              normalizeText={true}
                              defaultValue={firstnameField.defaultValue}
                              {...field}
                              onChange={firstnameField.onChange}
                              onBlur={firstnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={genderField.path}
                          label={genderField.label}
                          required={genderField.required}
                          disabled={genderField.disabled}
                        >
                          {(field) => (
                            <Input
                              type="radio"
                              variant="group"
                              label={genderField.label}
                              values={[
                                {
                                  label:
                                    SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS
                                      .MALE,
                                  value: GENDER.MALE,
                                },
                                {
                                  label:
                                    SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS
                                      .FEMALE,
                                  value: GENDER.FEMALE,
                                },
                              ]}
                              icon={<MdOutlineBadge size={22} />}
                              value={get(initialValues, genderPath)}
                              {...field}
                              onChange={genderField.onChange}
                              onBlur={genderField.onBlur}
                              optional={!genderField.required}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                          icon={<MdOutlineCake size={16} />}
                          placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                          type="date"
                          variant="simple"
                          defaultValue={dayjs(
                            get(
                              initialValues,
                              `children.${index}.identity.user.birthDate`,
                            ),
                          )}
                          format={DATE_FORMAT}
                          disabled
                        />
                      </Col>
                      {isMajor && (
                        <Col lg={24} xs={24}>
                          <FormField
                            path={socialSecurityNumberField.path}
                            label={socialSecurityNumberField.label}
                            required={socialSecurityNumberField.required}
                            disabled={socialSecurityNumberField.disabled}
                          >
                            {(field) => (
                              <Input
                                label={socialSecurityNumberField.label}
                                icon={<MdOutlineSubtitles size={16} />}
                                type="custom"
                                trim={true}
                                placeholder={
                                  SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER
                                    .PLACEHOLDER
                                }
                                defaultValue={
                                  socialSecurityNumberField.defaultValue
                                }
                                {...field}
                                onChange={socialSecurityNumberField.onChange}
                                onBlur={socialSecurityNumberField.onBlur}
                                optional={!socialSecurityNumberField.required}
                              />
                            )}
                          </FormField>
                        </Col>
                      )}
                    </Row>
                  </Form>
                </Col>
              );
            })}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
